/**
 * ADD RAISE DEVIATION CAPA COMPONENT
 * 
 * PURPOSE:
 * Modal form component for creating new deviations and managing CAPA (Corrective & Preventive Actions).
 * Handles both deviation raising and CAPA editing workflows with comprehensive validation.
 * 
 * COMPONENT MODES:
 * 1. ADD MODE: Create new deviation with auto-generated deviation number
 * 2. EDITCAPA MODE: Edit existing deviation and manage associated CAPA
 * 
 * FORM SECTIONS:
 * 
 * A) DEVIATION DETAILS:
 * - Slip Number: Optional dropdown, auto-fills related fields from WI data
 * - Project/Plant/Joint Numbers: Manual entry with alphanumeric validation
 * - Deviation Number: Auto-generated sequential number (read-only)
 * - Welder Code: Validates against welder master database
 * - Supervisor Code (MS): Dropdown from Manufacturing Supervisor role
 * - Observer Code: Dropdown from WE/QCE roles with department auto-fill
 * - Observation Date/Time: Current date (read-only) + required time entry
 * - Deviation Category: Predefined categories with description auto-fill
 * - Severity Level: 1-5 scale selection
 * - Deviation Text: Required description (max 500 chars)
 * - Image Upload: Optional file upload (JPEG/PNG/JPG, max 1MB)
 * 
 * B) CAPA SECTION (EditCapa mode only):
 * - Corrective Action: Required immediate fix description
 * - Root Cause Analysis: Required root cause identification
 * - Preventive Action: Required future prevention measures
 * - Action Taken By: Supervisor selection
 * - Action Date/Time: Date and time of action implementation
 * - Status: Open/Closed/Reject with workflow implications
 * 
 * VALIDATION LOGIC:
 * 
 * DEVIATION VALIDATION:
 * - Mandatory: Deviation text, supervisor code, observer code, time, category, severity
 * - Welder code validation against master data
 * - Alphanumeric validation for project/plant/joint numbers
 * - File format and size validation for images
 * 
 * CAPA VALIDATION:
 * - Mandatory: Corrective action, root cause analysis, preventive action, action time
 * - Date validation (action date >= observation date)
 * - Status-based field enabling/disabling
 * 
 * BUSINESS LOGIC:
 * 
 * SLIP NUMBER INTEGRATION:
 * - When slip number selected, auto-populates: project, plant, joint, welder details
 * - Fetches data from WI (Work Instruction) services
 * - Reduces manual entry and ensures data consistency
 * 
 * ROLE-BASED DROPDOWNS:
 * - Supervisor: Filters users with Manufacturing Supervisor role
 * - Observer: Filters users with WE (Welding Engineer) or QCE (Quality Control Engineer) roles
 * - Department auto-fill based on selected user's role
 * 
 * FILE UPLOAD:
 * - Drag-drop support with visual feedback
 * - File validation (format: JPEG/PNG/JPG, size: max 1MB)
 * - AWS S3 presigned URL for secure upload
 * - Download functionality for existing attachments
 * 
 * STATUS WORKFLOW:
 * - New deviation: "Pending for CAPA Analysis" (default)
 * - CAPA Open: "Pending for Closure"
 * - CAPA Closed: "Closed" (sends closure email)
 * - CAPA Reject: "Rejected" (sends rejection email)
 * 
 * EMAIL NOTIFICATIONS:
 * - Automatic email triggers on status changes
 * - Closure/rejection emails sent to stakeholders
 * - Email content includes deviation and CAPA details
 * 
 * L+1 SUPERVISOR INTEGRATION:
 * - creatorsL1: Auto-assigned L+1 supervisor email from user hierarchy
 * - creatorsL1Name: L+1 supervisor name for escalation
 * - creatorsL1SalCode: L+1 supervisor salary code for identification
 * - Fetched from props.usersL1Result[0] during deviation creation
 * - Used for management escalation and approval workflows
 * - Ensures proper reporting hierarchy in deviation management
 * 
 * DATA PERSISTENCE:
 * - Deviation data stored with audit trail (created/updated by/on)
 * - CAPA data linked via deviation number
 * - L+1 supervisor details stored for escalation workflow
 * - Temporary save functionality for CAPA (capaTempFlg)
 * - Version control through lastUpdatedBy/On fields
 */
import { useState, useEffect, useRef } from "react";
import { Col, Form, Modal, Row, Alert } from "react-bootstrap";
import { RaiseDeviation, downloadAttachment, getDeviationCategories, addCapa, sendClosureEmail } from "../../Services/deviationcapa-service";
import SuccessAlertModal from "../Modals/successAlertModal";
import ConfirmAlertModal from "../Modals/confirmAlertModal";
import { useNavigate } from "react-router-dom";
import Validate, { isAlphanumericWithDash, isAlphanumericWithDashSlash, isAlphanumericWithDotSpaceDashCommaBracketsSlashAmpersand } from "../../functions/validations";
import { getCurrentDateTime, dateCompare } from "../../functions/welding-functions";
import Button from "react-bootstrap/Button";
import Delete from "../../icons/DeleteIcon.svg";
import DeleteGrey from "../../icons/Delete_gray.svg";
import axios from "axios";
import { getAllUsersByRole } from "../../Services/user-service";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { getRoleDescByRoleId } from "../../Services/role-service";
import { getAllOnlySlipNos, getWIDataHeader } from "../../Services/WI-services";
import { getPresignedURLDev, getAllWelder } from "../../Services/Welder-services";
import { showSuccess, showError } from "../utility/ui/notifications/Toaster";
import { DeviationSeverityLevel } from "../utility/common/TransactionComponents/TransactionEntity";
import { createDropdown, refreshDropdown } from "../utility/common/DropdownComponent/DropdownHelper";

const AddRaiseDeviationCapa = (props) => {
  if (props.rowdata?.deviationstatus === "Active") {
    props.rowdata.deviationstatus = true;
  } else if (props.rowdata?.deviationstatus === "Inactive") {
    props.rowdata.deviationstatus = false;
  }

  let action = props.action;
  let formName = "Deviation";
  let todayDate = getCurrentDateTime("-");
  const [showSuccessModel, setShowSuccessModel] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showConfirmModel, setshowConfirmModel] = useState(false);
  const [slipNumberResult, setSlipNumberResult] = useState([]);
  const [observerCodeResult, setObserverCodeResult] = useState([]);
  const [deviationCategoryResult, setDeviationCategoryResult] = useState([]);
  const [supervisorCodeResult, setSupervisorCodeResult] = useState([]);
  const [alertMessage, setAlertMessage] = useState("");
  const [file, setFile] = useState([]);
  const [metadata, setMetadata] = useState("");
  const history = useNavigate();
  let username = sessionStorage.getItem("UserName");
  let userid = sessionStorage.getItem("UserId");
  let userrole = sessionStorage.getItem("Role");
  const [weldEnggResult, setWeldEnggResult] = useState([]);

  const handleOnDragOver = (event) => {
    event.preventDefault();
  };

  const [deviationObj, setDeviationObj] = useState(
    action === "Editcapa"
      ? props.rowdata
      : {
        PK: "deviation",
        SK: "deviationNumber#",
        slipNumber: "",
        projectNumber: "",
        deviationNumber: "",
        plantNumber: "",
        jointNumber: "",
        welderCode: "",
        welderName: "",
        supervisorCode: "",
        supervisorName: "",
        supervisorDept: "",
        observerCode: "",
        observerName: "",
        observerDept: "",
        observationDate: "",
        observationTime: "",
        deviationCategory: "",
        deviationCategoryDesc: "",
        deviationSeverityLevel: "",
        deviationText: "",
        deviationImageFile: "",
        devstatus: "Pending for CAPA Analysis",
        rejectstatus: "",
        "ageing(days)": 0,
        updatedBy: "",
        updatedByName: "",
        updatedOn: "",
        createdBy: "",
        createdByName: "",
        createdOn: "",
      }
  );

  const [capaObj, setCapaObj] = useState(
    action === "Editcapa" && Object.keys(props.caparowdata).length !== 0
      ? props.caparowdata
      : {
        PK: "deviation",
        SK: "capa#deviationNumber#",
        deviationNumber: "",
        correctiveAction: "",
        rootCauseAnalysis: "",
        preventiveAction: "",
        actionTakenBy: deviationObj.supervisorCode,
        actionTakenDate: todayDate,
        actionTimeTaken: "",
        lastUpdatedBy: username + " (" + userid + ")",
        lastUpdatedOn: "",
        capaStatus: "Open",
        capaTempFlg: true,
        updatedBy: "",
        updatedOn: "",
        createdBy: "",
        createdOn: "",
      }
  );

  const [error, setError] = useState({
    projectNumber: {},
    plantNumber: {},
    jointNumber: {},
    welderCode: {},
    supervisorCode: {},
    observerCode: {},
    observationDate: {},
    observationTime: {},
    deviationCategory: {},
    deviationSeverityLevel: {},
    deviationText: {},
    deviationImageFile: {},
  });

  const [capaerror, setCapaError] = useState({
    correctiveAction: {},
    rootCauseAnalysis: {},
    preventiveAction: {},
    actionTakenBy: {},
    actionTakenDate: {},
    actionTimeTaken: {},
    capaStatus: {},
  });

  const modalOnClick = () => {
    history.push({
      pathname: "/",
      data: "setProject",
    });
  };

  const handleCloseSuccess = () => {
    setShowSuccessModel(false);
    props.onHide();
    props.setReload(!props.reload);
  };

  const handleCloseConf = async () => {
    setshowConfirmModel(false);
  };

  const handleChange = async (e) => {
    const { name, value } = e.target;
    if (e.target.name === "welderCode") {
      let result = await getAllWelder();
      if (result?.length > 0) {
        let chk = result.filter(
          (x) =>
            x.wld_salarycode === value ||
            x.wld_code.toLowerCase() === value.toLowerCase()
        );
        if (chk?.length > 0) {
          setDeviationObj((prevState) => ({
            ...prevState,
            [name]: value,
            welderName: chk[0].wld_name,
          }));
          setError((prevState) => ({
            ...prevState,
            [name]: {
              status: false,
              message: "",
            },
          }));
        } else {
          setDeviationObj((prevState) => ({
            ...prevState,
            [name]: value,
          }));
          setError((prevState) => ({
            ...prevState,
            [name]: {
              status: true,
              message: "Enter valid welder code.",
            },
          }));
        }
      }
    } else if (e.target.name === "projectNumber") {
      e.target.value = ("" + e.target.value).toUpperCase();
      setDeviationObj((prevState) => ({
        ...prevState,
        [name]: e.target.value,
      }));
    } else {
      setDeviationObj((prevState) => ({
        ...prevState,
        [name]: value,
      }));
      setError((prevState) => ({
        ...prevState,
        [name]: {},
      }));
    }
  };

  const handleCapaChange = (e) => {
    const { name, value } = e.target;
    setCapaObj((prevState) => ({
      ...prevState,
      [name]: value,
    }));
    setCapaError((prevState) => ({
      ...prevState,
      [name]: {},
    }));
  };

  const validateDeviation = (action) => {

    let isError = false;
    if (deviationObj.deviationText.trim() === "") {
      deviationObj.deviationText = "";
      isError = true;
      error.deviationText = {
        status: true,
        message: "Deviation text is required.",
      };
    }
    if (error.welderCode.status && deviationObj.slipNumber === "") {
      deviationObj.welderCode = "";
      isError = true;
      error.welderCode = {
        status: true,
        message: "Enter valid welder code.",
      };
    }
    if (deviationObj.welderCode.trim() !== "") {
      let error = isAlphanumericWithDashSlash(deviationObj.welderCode);
      if (error.status) {
        deviationObj.welderCode = "";
        isError = true;
        error.welderCode = {
          status: true,
          message: "Enter valid welder code. Only - and / are allowed.",
        };
      }
    } else {
      deviationObj.welderCode = "";
      error.welderCode = {
        status: false,
        message: "",
      };
    }

    if (
      error.plantNumber.status ||
      error.supervisorCode.status ||
      error.projectNumber.status
    ) {
      isError = true;
    }
    return isError;
  };

  const validateCapa = (action) => {

    let isError = false;
    if (capaObj.correctiveAction.trim() === "") {
      capaObj.correctiveAction = "";
      isError = true;
      capaerror.correctiveAction = {
        status: true,
        message: "Corrective Action is required.",
      };
    }
    else if (capaerror.correctiveAction.status) {
      capaObj.correctiveAction = "";
      isError = true;
      capaerror.correctiveAction = {
        status: true,
        message: capaerror.correctiveAction.message,
      };
    } else {
      capaerror.correctiveAction = {
        status: false,
        message: "",
      };
    }
    if (capaObj.rootCauseAnalysis.trim() === "") {
      capaObj.rootCauseAnalysis = "";
      isError = true;
      capaerror.rootCauseAnalysis = {
        status: true,
        message: "Root cause analysis is required.",
      };
    }
    else if (capaerror.rootCauseAnalysis.status) {
      capaObj.rootCauseAnalysis = "";
      isError = true;
      capaerror.rootCauseAnalysis = {
        status: true,
        message: capaObj.rootCauseAnalysis.message,
      };
    } else {
      capaerror.rootCauseAnalysis = {
        status: false,
        message: "",
      };
    }
    if (capaObj.preventiveAction.trim() === "") {
      capaObj.preventiveAction = "";
      isError = true;
      capaerror.preventiveAction = {
        status: true,
        message: "Preventive Action is required.",
      };
    }
    if (capaerror.preventiveAction.status) {
      capaObj.preventiveAction = "";
      isError = true;
      capaerror.preventiveAction = {
        status: true,
        message: capaerror.preventiveAction.message,
      };
    } else {
      capaerror.preventiveAction = {
        status: false,
        message: "",
      };
    }
    if (capaObj.actionTimeTaken === "") {
      capaObj.actionTimeTaken = "";
      isError = true;
      capaerror.actionTimeTaken = {
        status: true,
        message: "Action Time Taken is required.",
      };
    } else {
      capaerror.actionTimeTaken = {
        status: false,
        message: "",
      };
    }

    setCapaError((prevState) => ({
      ...prevState,
      ...capaerror,
    }));

    if (
      capaerror.correctiveAction.status ||
      capaerror.rootCauseAnalysis.status ||
      capaerror.preventiveAction.status ||
      capaerror.actionTimeTaken.status
    ) {
      isError = true;
    }

    return isError;
  };

  const handleDateChange = (value, name) => {
    let result = false;
    if (name === "lastUpdatedOn" && deviationObj.observationDate !== "") {
      result = dateCompare(value, deviationObj.observationDate);
    }
    if (result) {
      setCapaError((prev) => ({
        ...prev,
        lastUpdatedOn: {
          status: true,
          message:
            "To date should be greater than or equal to Observation Date",
        },
      }));
    } else if (capaObj.lastUpdatedOn !== "") {
      setError((prev) => ({
        ...prev,
        lastUpdatedOn: {
          status: false,
          message: "",
        },
      }));
    }
    if (deviationObj.observationDate !== "") {
      setError((prev) => ({
        ...prev,
        observationDate: {
          status: false,
          message: "",
        },
      }));
    }
    setCapaObj((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleAdd = async (e) => {
    let result;
    if (action === "Add") {
      deviationObj.SK = "deviationNumber#" + deviationObj.deviationNumber;
      deviationObj.createdBy = userid;
      deviationObj.updatedBy = userid;
      deviationObj.updatedByName = username;
      deviationObj.createdByName = username;
      deviationObj.createdOn = getCurrentDateTime("-");
      deviationObj.updatedOn = getCurrentDateTime("-");
      deviationObj.observationDate = getCurrentDateTime("-");
      deviationObj.creatorsL1 = props.usersL1Result[0] != undefined ? props.usersL1Result[0]?.empEmailId : "";
      deviationObj.creatorsL1Name = props.usersL1Result[0] != undefined ? props.usersL1Result[0]?.empshortName : "";
      deviationObj.creatorsL1SalCode = props.usersL1Result[0] != undefined ? props.usersL1Result[0]?.empSalCode : "";
      deviationObj.weldingEnggId = weldEnggResult;
      result = await RaiseDeviation(deviationObj);
    } else {
      deviationObj.updatedBy = userid;
      deviationObj.updatedByName = username;
      deviationObj.updatedOn = getCurrentDateTime("-");
    }
    if (result.status) {
      if (metadata !== "") {
        const imgresult = await getPresignedURLDev(
          "Deviations",
          deviationObj.deviationNumber,
          deviationObj.deviationImageFile
        );
        uploadFiles(imgresult.url);
      }
      showSuccess(`Deviation details are added successfully`);
      props.setReload(!props.reload);
      setShowForm(false);
      props.onHide();
    } else {
      showError(`Deviation details are not added`);
      props.setReload(!props.reload);
      setShowForm(false);
      props.onHide();
    }
  }


  const handleDeviaitonSeverityChange = async (e) => {
    const { name, value } = e.target;
    setDeviationObj((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleFile = async (files) => {


    let isError = false;
    const newFile = files;
    setMetadata(files)
    let fileError = Validate(newFile);
    if (fileError === "") {
      setFile(newFile);

      let fileName = files.name;
      setDeviationObj((prevState) => ({
        ...prevState,
        deviationImageFile: fileName,
      }));
      setError((prevState) => ({
        ...prevState,
        deviationImageFile: { status: false, message: "" },
      }));
    } else {
      setError((prevState) => ({
        ...prevState,
        deviationImageFile: { status: true, message: fileError },
      }));
      return;
    }
    return isError;
  };

  const handleCapaStatusChange = (e) => {
    const { name, value } = e.target;
    setCapaObj((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const fileInputRef = useRef(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateDeviation(action)) {
      setshowConfirmModel(true);
    }
  };

  const handleReset = () => {
    let result = props.alldeviationsdata;
    let maxval = 0;
    if (result === undefined || result?.length === 0) {
      maxval = 1;
    } else {
      maxval = Math.max(...(result?.map((o) => o.deviationNumber) || [])) + 1;
    }
    setDeviationObj({
      PK: "deviation",
      SK: "deviationNumber#",
      slipNumber: "",
      projectNumber: "",
      deviationNumber: maxval,
      plantNumber: "",
      jointNumber: "",
      welderCode: "",
      welderName: "",
      supervisorCode: "",
      supervisorName: "",
      supervisorDept: "",
      observerCode: "",
      observerName: "",
      observerDept: "",
      observationDate: "",
      observationTime: "",
      deviationCategory: "",
      deviationCategoryDesc: "",
      deviationSeverityLevel: "",
      deviationText: "",
      deviationImageFile: "",
      devstatus: "Pending for CAPA Analysis",
      rejectstatus: "",
      "ageing(days)": 0,
      updatedBy: "",
      updatedByName: "",
      updatedOn: "",
      createdBy: "",
      createdByName: "",
      createdOn: "",
    });
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setAlertMessage("");
    }, 10000);
    return () => clearTimeout(timer);
  }, [alertMessage]);

  const handleOndrop = (event) => {
    let imageFile = event.dataTransfer.files[0];
    handleFile(imageFile);
  };

  const handleCapaResult = (result, buttonName) => {
    if (result?.status) {
      if (buttonName === "save" && capaObj.capaStatus === "Closed") {
        sendClosureEmail(deviationObj, capaObj);
      }
      if (buttonName === "save" && capaObj.capaStatus === "Reject") {
        sendClosureEmail(deviationObj, capaObj);
      }
      showSuccess(`Capa details are added successfully`);
      props.setReload(!props.reload);
      setShowForm(false);
      props.onHide();
    } else {
      showError(`Capa details are not added`);
    }
  };

  const updateDeviationStatus = async () => {
    const commonUpdate = {
      updatedBy: userid,
      updatedOn: todayDate
    };

    if (capaObj.capaStatus === "Closed") {
      capaObj.capaTempFlg = false;
      Object.assign(deviationObj, commonUpdate, { devstatus: "Closed" });
    } else if (capaObj.capaStatus === "Reject" && deviationObj.rejectstatus !== "yes") {
      capaObj.capaTempFlg = true;
      Object.assign(deviationObj, commonUpdate, { devstatus: "Rejected", rejectstatus: "yes" });
    } else if (capaObj.capaStatus === "Open" || (deviationObj.devstatus === "Rejected" && deviationObj.rejectstatus === "yes")) {
      capaObj.capaTempFlg = false;
      Object.assign(deviationObj, commonUpdate, { devstatus: "Pending for Closure" });
    }

    return await RaiseDeviation(deviationObj);
  };

  const updateCapaData = async () => {
    if (action === "Editcapa") {
      Object.assign(capaObj, {
        SK: "capa#deviationNumber#" + deviationObj.deviationNumber,
        deviationNumber: deviationObj.deviationNumber,
        createdBy: userid,
        updatedBy: userid,
        createdOn: todayDate,
        updatedOn: todayDate,
        lastUpdatedOn: todayDate
      });
      return await addCapa(capaObj);
    }
    return null;
  };

  const handleSaveCapa = async (e) => {
    const buttonName = e.target.name;

    if (buttonName === "save") {
      if (!validateCapa(action)) {
        const result = await updateDeviationStatus();
        const capaResult = await updateCapaData();
        handleCapaResult(capaResult || result, buttonName);
      }
    } else if (buttonName === "tempsave") {
      capaObj.capaTempFlg = true;
      const result = await updateCapaData();
      handleCapaResult(result, buttonName);
    }
  };

  const uploadFiles = async (signedURL) => {
    try {
      if (signedURL) {
        await axios.put(signedURL, file);
      }
      return "";
    } catch (error) {
      return " Error in file upload.";
    }
  };

  const handleDownloadDocument = async (fileName) => {
    try {
      await downloadAttachment({
        foldername: "Deviations",
        subfoldername: deviationObj.deviationNumber,
        filename: fileName
      });
    } catch (err) {
      console.error("Error downloading document:", err);
      showError(`Error in fetching file.`);
    }
  };

  const handleDeleteDocEvent = async (fileName) => {
    setDeviationObj((prevState) => ({
      ...prevState,
      deviationImageFile: "",
    }));
  };

  const handleSlipNumberChange = async (value) => {
    const WIresult = await getWIDataHeader({ WI_Slip: value });
    if (WIresult?.length > 0) {
      setDeviationObj((prevState) => ({
        ...prevState,
        slipNumber: WIresult[0].WI_Slip,
        jointNumber: WIresult[0].WI_Joint_No,
        plantNumber: WIresult[0].WI_plant,
        projectNumber: WIresult[0].WI_Project_No,
        welderCode: WIresult[0].WI_Welder_Id,
        welderName: WIresult[0].WI_Welder_Name,
      }));
    }
  };

  const handleSupervisorChange = async (e, value) => {
    const nm = e.target[e.target.selectedIndex].getAttribute("data-key");
    const roledesc = await getRoleDescByRoleId(
      e.target.children[e.target.selectedIndex].getAttribute("result")
    );
    const roledesc1 = roledesc?.length > 0 ? roledesc[0].rm_name : "";

    setDeviationObj((prevState) => ({
      ...prevState,
      supervisorCode: value,
      supervisorName: nm,
      supervisorDept: roledesc1,
    }));
  };

  const handleObserverChange = async (e, value) => {
    const nm = e.target[e.target.selectedIndex].getAttribute("data-key");
    const roledesc = await getRoleDescByRoleId(
      e.target.children[e.target.selectedIndex].getAttribute("result")
    );
    const roledesc1 = roledesc?.length > 0 ? roledesc[0].rm_name : "";

    setDeviationObj((prevState) => ({
      ...prevState,
      observerCode: value,
      observerName: nm,
      observerDept: roledesc1,
    }));
  };

  const handleDeviationCategoryChange = (e, name, value) => {
    if (value !== "") {
      setDeviationObj((prevState) => ({
        ...prevState,
        [name]: value,
        deviationCategoryDesc:
          e.target.children[e.target.selectedIndex].getAttribute("result"),
      }));
    } else {
      setDeviationObj((prevState) => ({
        ...prevState,
        [name]: "",
        deviationCategoryDesc: "",
      }));
    }
  };

  const handleDropDownChange = async (e) => {
    const { name, value } = e.target;

    switch (name) {
      case "slipNumber":
        await handleSlipNumberChange(value);
        break;
      case "supervisorCode":
        await handleSupervisorChange(e, value);
        break;
      case "observerCode":
        await handleObserverChange(e, value);
        break;
      case "deviationCategory":
        handleDeviationCategoryChange(e, name, value);
        break;
      default:
        setDeviationObj((prevState) => ({
          ...prevState,
          [name]: value,
        }));
    }

    setError((prevState) => ({
      ...prevState,
      [name]: {},
    }));
  };

  const fetchSlipNumbers = async () => {
    let result = await getAllOnlySlipNos();
    result?.sort(sortByPropertyInAscending("WI_Slip"));
    let temp = [];
    if (result !== undefined) {
      for (let item of result) {
        temp.push({ Name: item.WI_Slip, Code: item.WI_Slip });
      }
    }
    setSlipNumberResult(temp);
  };

  const fetchSupervisorCode = async () => {
    let roles = [{ role: process.env.REACT_APP_ROLE_Manufacturingsupervisor }];
    let result = await getAllUsersByRole(roles);
    let temp = [];
    if (result !== undefined) {
      for (let item of result) {
        temp.push({ Name: item.um_name, Code: item.SK, Dept: item.um_role });
      }
    }
    setSupervisorCodeResult(temp);
  };

  const fetchUsersWE = async () => {
    let roles = [{ role: process.env.REACT_APP_ROLE_WEDLINGENGINEER }];
    let result = await getAllUsersByRole(roles);

    let temp = [];
    if (result !== undefined) {
      for (let item of result) {
        temp.push(item.SK);
      }
    }
    setWeldEnggResult(temp);
  };


  const fetchObserverCode = async () => {
    let roles = [
      { role: process.env.REACT_APP_ROLE_WEDLINGENGINEER },
      { role: process.env.REACT_APP_ROLE_QCENGINEER },
    ];
    let result = await getAllUsersByRole(roles);
    let temp = [];
    if (result !== undefined) {
      for (let item of result) {
        temp.push({ Name: item.um_name, Code: item.SK, Dept: item.um_role });
      }
    }
    setObserverCodeResult(temp);
  };

  const fetchDeviationCategory = async () => {
    let result = await getDeviationCategories();
    const createDropdownList = createDropdown("Deviation Category");
    await refreshDropdown("Deviation Category");
    let temp = [];
    for (let item of createDropdownList) {
      let match = result?.find(r => r.devCategory === item.name?.trim());
      temp.push({
        Name: item.name,
        Code: item.name,
        Value: item.value,
        CategoryDesc: match?.devCategoryDesc || "",
      });
    }
    console.log(temp, "temp");
    setDeviationCategoryResult(temp);
  };

  useEffect(() => {
    let result = props.alldeviationsdata;
    let maxval = 0;
    if (result === undefined || result?.length === 0) {
      maxval = 1;
    } else {
      maxval = Math.max(...(result?.map((o) => o.deviationNumber) || [])) + 1;
    }
    if (action === "Add") {
      setDeviationObj((prevState) => ({
        ...prevState,
        deviationNumber: maxval,
      }));
    }
    let caparesult = props.caparowdata;
    if (Object.keys(caparesult).length !== 0) {
      setCapaObj(caparesult);
    }
    fetchSlipNumbers();
    fetchSupervisorCode();
    fetchObserverCode();
    fetchDeviationCategory();
    fetchUsersWE();
  }, []);

  function sortByPropertyInAscending(property) {
    return function (a, b) {
      if (Number(a[property]) > Number(b[property])) return 1;
      else if (Number(a[property]) < Number(b[property])) return -1;
      return 0;
    };
  }

  return (
    <>
      <SuccessAlertModal
        clickFunction={modalOnClick}
        msg={`Great! You've ${action}ed Project successfully.`}
        show={showSuccessModel}
        handleClose={handleCloseSuccess}
      />
      <ConfirmAlertModal
        action={action}
        handleYes={handleAdd}
        formName={formName}
        show={showConfirmModel}
        handleClose={handleCloseConf}
      />

      <Modal
        {...props}
        size="lg"
        centered
        className={
          showForm || showSuccessModel || showConfirmModel
            ? "opacity-0 userMasterModal"
            : "opacity-100 userMasterModal"
        }
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>Raise Deviation</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit} onReset={handleReset} autoComplete="off">
          <Modal.Body>
            <div className="px-2 pb-2">
              <Row className="gx-3">
                <Col md={4} xs={12}>
                  <Form.Group className="g-select">
                    <div className="d-grid align-items-center">
                      <Form.Select
                        id="slipNumber"
                        name="slipNumber"
                        type="text"
                        placeholder=" "
                        value={deviationObj.slipNumber}
                        onChange={(e) => {
                          handleDropDownChange(e);
                        }}
                        disabled={action === "Editcapa"}
                      >
                        <option value="">Select</option>
                        {slipNumberResult
                          ? slipNumberResult.map((slipNum) => (
                            <option key={slipNum.Code} value={slipNum.Name}>
                              {slipNum.Name}
                            </option>
                          ))
                          : null}
                      </Form.Select>
                      <Form.Label htmlFor="slipNumber">
                        Slip Number <span className="italic">(Optional)</span>
                      </Form.Label>
                    </div>
                  </Form.Group>
                </Col>
                <Col lg={4} md={4} xs={12}>
                  <Form.Group className="g-input">
                    <div className="d-grid align-items-center">
                      <Form.Control
                        id="projectNumber"
                        name="projectNumber"
                        type="text"
                        maxLength="10"
                        readOnly={action === "Editcapa" || deviationObj.slipNumber !== ""}
                        placeholder=" "
                        value={deviationObj.projectNumber}
                        onChange={handleChange}
                        onBlur={(e) => {
                          let error = isAlphanumericWithDash(e.target.value);
                          setError((prevState) => ({
                            ...prevState,
                            projectNumber: error,
                          }));
                        }}
                      />
                      <Form.Label htmlFor="projectNumber">Project Number</Form.Label>
                      {error.projectNumber.status && (
                        <Form.Text className="text-danger">
                          {error.projectNumber.message}
                        </Form.Text>
                      )}
                    </div>
                  </Form.Group>
                </Col>
                <Col lg={4} md={4} xs={12}>
                  <Form.Group className="g-input">
                    <div className="d-grid align-items-center">
                      <Form.Control
                        id="deviationNumber"
                        name="deviationNumber"
                        type="text"
                        maxLength="50"
                        readOnly
                        value={deviationObj.deviationNumber}
                      />
                      <Form.Label htmlFor="deviationNumber">Deviation Number</Form.Label>
                    </div>
                  </Form.Group>
                </Col>
                <Col lg={4} md={4} xs={12}>
                  <Form.Group className="g-input">
                    <div className="d-grid align-items-center">
                      <Form.Control
                        id="plantNumber"
                        name="plantNumber"
                        type="text"
                        maxLength="10"
                        placeholder=" "
                        value={deviationObj.plantNumber}
                        onChange={handleChange}
                        readOnly={action === "Editcapa" || deviationObj.slipNumber !== ""}
                        onBlur={(e) => {
                          let error = isAlphanumericWithDashSlash(
                            e.target.value
                          );
                          setError((prevState) => ({
                            ...prevState,
                            plantNumber: error,
                          }));
                        }}
                      />
                      <Form.Label htmlFor="plantNumber">Plant Number</Form.Label>
                      {error.plantNumber.status && (
                        <Form.Text className="text-danger">
                          {error.plantNumber.message}
                        </Form.Text>
                      )}
                    </div>
                  </Form.Group>
                </Col>
                <Col lg={4} md={4} xs={12}>
                  <Form.Group className="g-input">
                    <div className="d-grid align-items-center">
                      <Form.Control
                        id="jointNumber"
                        name="jointNumber"
                        type="text"
                        maxLength="10"
                        placeholder=" "
                        value={deviationObj.jointNumber}
                        onChange={handleChange}
                        readOnly={action === "Editcapa" || deviationObj.slipNumber !== ""}
                        onBlur={(e) => {
                          let error = isAlphanumericWithDashSlash(
                            e.target.value
                          );
                          setError((prevState) => ({
                            ...prevState,
                            jointNumber: error,
                          }));
                        }}
                      />
                      <Form.Label htmlFor="jointNumber">Joint Number</Form.Label>
                      {error.jointNumber.status && (
                        <Form.Text className="text-danger">
                          {error.jointNumber.message}
                        </Form.Text>
                      )}
                    </div>
                  </Form.Group>
                </Col>
                <Col lg={4} md={4} xs={12}>
                  <Form.Group className="g-input">
                    <div className="d-grid align-items-center">
                      <Form.Control
                        id="welderCode"
                        name="welderCode"
                        type="text"
                        maxLength="10"
                        readOnly={action === "Editcapa" || deviationObj.slipNumber !== ""}
                        placeholder=" "
                        value={deviationObj.welderCode}
                        onChange={handleChange}
                      />
                      <Form.Label htmlFor="welderCode">Welder Code</Form.Label>
                      {error.welderCode.status && (
                        <Form.Text className="text-danger">
                          {error.welderCode.message}
                        </Form.Text>
                      )}
                    </div>
                  </Form.Group>
                </Col>
                <Col lg={4} md={4} xs={12}>
                  <Form.Group className="g-select">
                    <div className="d-grid align-items-center">
                      <Form.Select
                        id="supervisorCode"
                        name="supervisorCode"
                        type="text"
                        style={{
                          whiteSpace: "break-spaces",
                          paddingLeft: "15px",
                        }}
                        required
                        placeholder=" "
                        value={deviationObj.supervisorCode}
                        onChange={(e) => {
                          handleDropDownChange(e);
                        }}
                        disabled={action === "Editcapa"}
                      >
                        <option value="">Select</option>
                        {supervisorCodeResult
                          ? supervisorCodeResult.map((supe) => (
                            <option
                              key={supe.Code}
                              result={supe.Dept}
                              data-key={supe.Name}
                              value={supe.Code}
                            >
                              {supe.Name + " (" + supe.Code + ")"}
                            </option>
                          ))
                          : null}
                      </Form.Select>
                      <Form.Label htmlFor="supervisorCode"> MS Code</Form.Label>
                    </div>
                  </Form.Group>
                </Col>
                <Col lg={4} md={4} xs={12}>
                  <Form.Group className="g-input">
                    <div className="d-grid align-items-center">
                      <Form.Control
                        id="supervisorDept"
                        name="supervisorDept"
                        type="text"
                        readOnly
                        placeholder=" "
                        value={deviationObj.supervisorDept}
                      />
                      <Form.Label htmlFor="supervisorDept">MS Department</Form.Label>
                    </div>
                  </Form.Group>
                </Col>
                <Col lg={4} md={4} xs={12}>
                  <Form.Group className="g-select">
                    <div className="d-grid align-items-center">
                      <Form.Select
                        id="observerCode"
                        name="observerCode"
                        type="text"
                        style={{
                          whiteSpace: "break-spaces",
                          paddingLeft: "15px",
                        }}
                        required
                        className={`observerCode ${error.observerCode.status
                          ? "placeHolderInput inputText text-field-with-error allow"
                          : "placeHolderInput inputText allow"
                          }`}
                        placeholder=" "
                        value={deviationObj.observerCode}
                        onChange={(e) => {
                          handleDropDownChange(e);
                        }}
                        disabled={action === "Editcapa"}
                      >
                        <option value="">Select</option>
                        {observerCodeResult
                          ? observerCodeResult.map((observe) => (
                            <option
                              key={observe.Code}
                              result={observe.Dept}
                              data-key={observe.Name}
                              value={observe.Name}
                            >
                              {observe.Name + " (" + observe.Code + ")"}
                            </option>
                          ))
                          : null}
                      </Form.Select>
                      <Form.Label htmlFor="observerCode">Observer Code</Form.Label>
                    </div>
                  </Form.Group>
                </Col>
                <Col lg={4} md={4} xs={12}>
                  <Form.Group className="g-input">
                    <div className="d-grid align-items-center">
                      <Form.Control
                        id="observerDept"
                        name="observerDept"
                        type="text"
                        readOnly
                        placeholder=" "
                        value={deviationObj.observerDept}
                      />
                      <Form.Label htmlFor="observerDept">Observer Department</Form.Label>
                    </div>
                  </Form.Group>
                </Col>
                <Col lg={4} xs={12}>
                  <div className="groove_date">
                    <div className="Date-input w-100">
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DemoContainer components={["DatePicker"]}>
                          <DatePicker
                            label="Observation Date"
                            value={
                              deviationObj.observationDate
                                ? dayjs(deviationObj.observationDate)
                                : dayjs(todayDate)
                            }
                            disabled
                            format="DD-MM-YYYY"
                          />
                        </DemoContainer>
                      </LocalizationProvider>
                    </div>
                  </div>
                </Col>
                <Col lg={4} md={4} xs={12}>
                  <Form.Group className="g-input">
                    <div className="d-grid align-items-center">
                      <Form.Control
                        id="observationTime"
                        name="observationTime"
                        type="Time"
                        maxLength="5"
                        required
                        readOnly={action === "Editcapa"}
                        placeholder=" "
                        value={deviationObj.observationTime}
                        onChange={handleChange}
                        onInput={(e) => {
                          let error = isAlphanumericWithDash(e.target.value);
                          setError((prevState) => ({
                            ...prevState,
                            observationTime: error,
                          }));
                        }}
                      />
                      <Form.Label htmlFor="observationTime">Observation Time</Form.Label>
                      {error.observationTime.status && (
                        <Form.Text className="text-danger">
                          {error.observationTime.message}
                        </Form.Text>
                      )}
                    </div>
                  </Form.Group>
                </Col>
                <Col lg={4} md={4} xs={12}>
                  <Form.Group className="g-select">
                    <div className="d-grid align-items-center">
                      <Form.Select
                        id="deviationCategory"
                        name="deviationCategory"
                        type="text"
                        className="deviationCategory"
                        required
                        placeholder=" "
                        value={deviationObj.deviationCategory}
                        onChange={(e) => {
                          handleDropDownChange(e);
                        }}
                        disabled={action === "Editcapa"}
                      >
                        {deviationCategoryResult
                          ? deviationCategoryResult.map(
                            (deviationCategoryResult) => (
                              <option
                                key={deviationCategoryResult.Code}
                                result={deviationCategoryResult.CategoryDesc}
                                value={deviationCategoryResult.Value}
                              >
                                {deviationCategoryResult.Name}
                              </option>
                            )
                          )
                          : null}
                      </Form.Select>
                      <Form.Label htmlFor="deviationCategory">Deviation Category</Form.Label>
                    </div>
                  </Form.Group>
                </Col>
                <Col lg={8} md={8} xs={12}>
                  <Form.Group className="g-input">
                    <div className="d-grid align-items-center">
                      <Form.Control
                        id="deviationCategoryDesc"
                        name="deviationCategoryDesc"
                        type="text"
                        readOnly
                        placeholder=" "
                        value={deviationObj.deviationCategoryDesc}
                      />
                      <Form.Label htmlFor="deviationCategoryDesc">
                        Deviation Category Description
                      </Form.Label>
                    </div>
                  </Form.Group>
                </Col>
                <Col lg={4} md={4} xs={12}>
                  <Form.Group className="g-select">
                    <div className="d-grid align-items-center">
                      <Form.Select
                        id="deviationSeverityLevel"
                        name="deviationSeverityLevel"
                        type="text"
                        placeholder=" "
                        required
                        value={deviationObj.deviationSeverityLevel}
                        onChange={(e) => {
                          handleDeviaitonSeverityChange(e);
                        }}
                        disabled={action === "Editcapa"}
                      >
                        {DeviationSeverityLevel.map((item) => {
                          return (
                            <option key={item.name} value={item.value}>{item.name}</option>
                          )
                        })}
                      </Form.Select>
                      <Form.Label htmlFor="deviationSeverityLevel">
                        Deviation Severity Level
                      </Form.Label>
                    </div>
                  </Form.Group>
                </Col>
                <Col lg={4}></Col>
                <Col lg={4}></Col>
                <Col lg={12} md={12} xs={12}>
                  <div className="T-input w-100">
                    <textarea
                      type="text"
                      name="deviationText"
                      style={{ height: "40px" }}
                      id="deviationText"
                      readOnly={action === "Editcapa"}
                      maxLength={500}
                      placeholder=" "
                      value={deviationObj.deviationText}
                      onChange={handleChange}
                      onBlur={(e) => {
                        let error =
                          isAlphanumericWithDotSpaceDashCommaBracketsSlashAmpersand(
                            e.target.value
                          );
                        setError((prevState) => ({
                          ...prevState,
                          deviationText: error,
                        }));
                      }}
                    />
                    <Form.Label htmlFor="deviationText">Deviation Text</Form.Label>
                    {error.deviationText.status && (
                      <Form.Text className="text-danger">
                        {error.deviationText.message}
                      </Form.Text>
                    )}
                  </div>
                </Col>
                <Col md={12} xs={12}>
                  <Form.Group>
                    {deviationObj.deviationImageFile === "" &&
                      action !== "Editcapa" ? (
                      <>
                        <Form.Label className="fileuploadTitle" >Upload Image</Form.Label>
                        <form encType="multipart/form-data" style={{ position: "relative", top: "10px" }} >
                          <div className="deviation-pic-upload" >
                            <div
                              className="deviation-drop-zone"
                              onDragOver={handleOnDragOver}
                              onDrop={handleOndrop}
                            >
                              <div>
                                <Form.Label
                                  className="deviation-pic-text-black"
                                  htmlFor="fileInput"
                                  style={{ fontSize: "14px", display: "inline" }}
                                >
                                  Upload image here or{" "}
                                </Form.Label>
                                <Form.Label
                                  className="deviation-pic-text-blue"
                                  htmlFor="fileInput"
                                  style={{ fontSize: "14px", display: "inline" }}
                                >
                                  Select a file
                                </Form.Label>
                              </div>
                              <Form.Label className="deviation-pic-text-weld" style={{ fontSize: "12px" }}
                              >
                                Supports: Jpeg, PNG and JPG, Size:Max 1MB
                              </Form.Label>
                              <Form.Label className="deviation-pic-text-optional italic" style={{ fontSize: "12px", position: "relative", bottom: "6px" }}
                              >
                                (Optional)
                              </Form.Label>
                              <input
                                id="fileInput"
                                name="fileInput"
                                type="file"
                                accept="image/*"
                                hidden
                                ref={fileInputRef}
                                onChange={(e) => handleFile(e.target.files[0])}
                              />
                            </div>
                          </div>
                        </form>
                      </>
                    ) : (
                      <>
                        <Form.Label className="fileuploadTitle">Upload Image</Form.Label>
                        <Col lg={12} md={12} xs={12}>
                          <Form.Label
                            className="add-new-link"
                            onClick={() =>
                              handleDownloadDocument(
                                deviationObj.deviationImageFile
                              )
                            }
                          >
                            {deviationObj.deviationImageFile === ""
                              ? "No file chosen"
                              : deviationObj.deviationImageFile}
                            &nbsp;&nbsp;&nbsp;
                          </Form.Label>
                          <img
                            onMouseOver={(e) =>
                              (e.currentTarget.src = Delete)
                            }
                            onMouseOut={(e) =>
                              (e.currentTarget.src = DeleteGrey)
                            }
                            src={DeleteGrey}
                            alt="Delete"
                            onClick={() =>
                              handleDeleteDocEvent(
                                deviationObj.deviationImageFile
                              )
                            }
                          />
                        </Col>
                      </>
                    )}
                    {error.deviationImageFile.status && (
                      <Form.Text className="text-danger">
                        {error.deviationImageFile.message}
                      </Form.Text>
                    )}
                  </Form.Group>
                </Col>
              </Row>
            </div>
            {alertMessage && (
              <Alert className="alertBox" variant="warning">
                {alertMessage}
              </Alert>
            )}
            <div className="px-2 pb-2">
              {action === "Editcapa" ? (
                <>
                  <Row>
                    <Col lg={12} md={12} xs={12}>
                      <div className="T-input w-100">
                        <textarea
                          type="text"
                          name="correctiveAction"
                          style={{ height: "40px" }}
                          id="correctiveAction"
                          readOnly={!capaObj.capaTempFlg}
                          maxLength={500}
                          placeholder=" "
                          value={capaObj.correctiveAction}
                          onChange={handleCapaChange}
                          onBlur={(e) => {
                            let error =
                              isAlphanumericWithDotSpaceDashCommaBracketsSlashAmpersand(
                                e.target.value
                              );
                            setCapaError((prevState) => ({
                              ...prevState,
                              correctiveAction: error,
                            }));
                          }}
                        />
                        <Form.Label htmlFor="correctiveAction">
                          Corrective Action
                        </Form.Label>
                        {capaerror.correctiveAction.status && (
                          <Form.Text className="text-danger">
                            {capaerror.correctiveAction.message}
                          </Form.Text>
                        )}
                      </div>
                    </Col>
                    <Col lg={12} md={12} xs={12}>
                      <div className="T-input w-100">
                        <textarea
                          type="text"
                          name="rootCauseAnalysis"
                          style={{ height: "40px" }}
                          id="rootCauseAnalysis"
                          maxLength={500}
                          readOnly={!capaObj.capaTempFlg}
                          placeholder=" "
                          value={capaObj.rootCauseAnalysis}
                          onChange={handleCapaChange}
                          onBlur={(e) => {
                            let error =
                              isAlphanumericWithDotSpaceDashCommaBracketsSlashAmpersand(
                                e.target.value
                              );
                            setCapaError((prevState) => ({
                              ...prevState,
                              rootCauseAnalysis: error,
                            }));
                          }}
                        />
                        <Form.Label htmlFor="rootCauseAnalysis">
                          Root Cause Analysis
                        </Form.Label>
                        {capaerror.rootCauseAnalysis.status && (
                          <Form.Text className="text-danger">
                            {capaerror.rootCauseAnalysis.message}
                          </Form.Text>
                        )}
                      </div>
                    </Col>
                    <Col lg={12} md={12} xs={12}>
                      <div className="T-input w-100">
                        <textarea
                          type="text"
                          name="preventiveAction"
                          style={{ height: "40px" }}
                          id="preventiveAction"
                          maxLength={500}
                          readOnly={!capaObj.capaTempFlg}
                          placeholder=" "
                          value={capaObj.preventiveAction}
                          onChange={handleCapaChange}
                          onBlur={(e) => {
                            let error =
                              isAlphanumericWithDotSpaceDashCommaBracketsSlashAmpersand(
                                e.target.value
                              );
                            setCapaError((prevState) => ({
                              ...prevState,
                              preventiveAction: error,
                            }));
                          }}
                        />
                        <Form.Label htmlFor="preventiveAction">
                          Prevention Action
                        </Form.Label>
                        {capaerror.preventiveAction.status && (
                          <Form.Text className="text-danger">
                            {capaerror.preventiveAction.message}
                          </Form.Text>
                        )}
                      </div>
                    </Col>
                    <Col lg={4} md={4} xs={12}>
                      <Form.Group className="g-select">
                        <div className="d-grid align-items-center">
                          <Form.Select
                            id="actionTakenBy"
                            name="actionTakenBy"
                            type="text"
                            style={{
                              whiteSpace: "break-spaces",
                              paddingLeft: "15px",
                            }}
                            required
                            placeholder=" "
                            value={deviationObj.actionTakenBy}
                            onChange={(e) => {
                              handleDropDownChange(e);
                            }}
                          >
                            <option value="">Select</option>
                            {supervisorCodeResult
                              ? supervisorCodeResult.map((supe) => (
                                <option
                                  key={supe.Code}
                                  result={supe.Dept}
                                  data-key={supe.Name}
                                  value={supe.Code}
                                >
                                  {supe.Name + " (" + supe.Code + ")"}
                                </option>
                              ))
                              : null}
                          </Form.Select>
                          <Form.Label htmlFor="supervisorCode">Action Taken By</Form.Label>
                        </div>
                      </Form.Group>
                    </Col>
                    <Col lg={4} md={4} xs={12}>
                      <div className="groove_date">
                        <div className="Date-input w-100">
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DemoContainer components={["DatePicker"]}>
                              <DatePicker
                                label="Action Taken Date"
                                value={
                                  capaObj.actionTakenDate
                                    ? dayjs(capaObj.actionTakenDate)
                                    : dayjs(todayDate)
                                }
                                disabled={!capaObj.capaTempFlg}
                                onChange={(newValue) => {
                                  let date =
                                    dayjs(newValue).format("YYYY-MM-DD");
                                  handleDateChange(date, "actionTakenDate");
                                }}
                                format="DD-MM-YYYY"
                              />
                            </DemoContainer>
                          </LocalizationProvider>
                          {capaerror.actionTakenDate.status && (
                            <Form.Text className="text-danger">
                              {capaerror.actionTakenDate.message}
                            </Form.Text>
                          )}
                        </div>
                      </div>
                    </Col>
                    <Col lg={4} md={4} xs={12}>
                      <Form.Group className="g-input">
                        <div className="d-grid align-items-center">
                          <Form.Control
                            id="actionTimeTaken"
                            name="actionTimeTaken"
                            type="Time"
                            maxLength="5"
                            required
                            readOnly={!capaObj.capaTempFlg}
                            placeholder=" "
                            value={capaObj.actionTimeTaken}
                            onChange={handleCapaChange}
                          />
                          <Form.Label htmlFor="actionTimeTaken">Action Time Taken</Form.Label>
                          {capaerror.actionTimeTaken.status && (
                            <Form.Text className="text-danger">
                              {capaerror.actionTimeTaken.message}
                            </Form.Text>
                          )}
                        </div>
                      </Form.Group>
                    </Col>
                    <Col lg={4} md={4} xs={12}>
                      <Form.Group className="g-input">
                        <div className="d-flex align-items-center">
                          <Form.Control
                            id="lastUpdatedBy"
                            name="lastUpdatedBy"
                            type="text"
                            maxLength="50"
                            readOnly
                            placeholder=" "
                            value={capaObj.lastUpdatedBy}
                          />
                          <Form.Label htmlFor="lastUpdatedBy">Last Updated By</Form.Label>
                        </div>
                      </Form.Group>
                    </Col>
                    <Col lg={4} md={4} xs={12}>
                      <div className="groove_date">
                        <div className="Date-input w-100">
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DemoContainer components={["DatePicker"]}>
                              <DatePicker
                                label="Last Updated Date"
                                value={
                                  capaObj.lastUpdatedOn
                                    ? dayjs(capaObj.lastUpdatedOn)
                                    : dayjs(todayDate)
                                }
                                readOnly
                                format="DD-MM-YYYY"
                              />
                            </DemoContainer>
                          </LocalizationProvider>
                        </div>
                      </div>
                    </Col>
                    <Col lg={4} md={4} xs={12}>
                      <Form.Group className="g-select">
                        <div className="d-grid align-items-center">
                          <Form.Select
                            id="capaStatus"
                            name="capaStatus"
                            type="text"
                            disabled={capaObj.capaTempFlg || !(userrole === "WE" && deviationObj.devstatus !== "Closed")}
                            placeholder=" "
                            required
                            value={capaObj.capaStatus}
                            onChange={(e) => {
                              handleCapaStatusChange(e);
                            }}
                          >
                            <option key="Open" value="Open">
                              Open
                            </option>
                            <option key="Closed" value="Closed">
                              Closed
                            </option>
                            <option key="Reject" value="Reject">
                              Reject
                            </option>
                          </Form.Select>
                          <Form.Label htmlFor="capaStatus">Status</Form.Label>
                          {capaerror.capaStatus.status && (
                            <Form.Text className="text-danger">
                              {capaerror.capaStatus.message}
                            </Form.Text>
                          )}
                        </div>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    {deviationObj.devstatus !== "Closed" ? (
                      <>
                        <Col>
                          <Button
                            type="button"
                            name="tempsave"
                            id="tempsave"
                            className="semiBoldToggleButton tempSaveBtn"
                            onClick={handleSaveCapa}
                          >
                            Temp Save
                          </Button>
                        </Col>
                        <Col>
                          <Button
                            type="button"
                            name="save"
                            id="save"
                            className="btn-add-color semiBoldToggleButton rightAlignedButton"
                            onClick={handleSaveCapa}
                          >
                            Save
                          </Button>
                        </Col>
                      </>
                    ) : (
                      <></>
                    )}
                  </Row>
                </>
              ) : (
                <Row className="formobile" style={{ position: "relative", top: "20px" }} >
                  <Col>
                    <Button
                      variant="outline-success"
                      type="reset"
                      className="semiBoldToggleButton tempSaveBtn"
                    >
                      Reset
                    </Button>
                  </Col>
                  <Col>
                    <Button
                      type="submit"
                      className="btn-add-color semiBoldToggleButton rightAlignedButton"
                    >
                      Raise
                    </Button>
                  </Col>
                </Row>
              )}
            </div>
          </Modal.Body>
          <Row>
            <br></br>
          </Row>
        </Form>
      </Modal>

    </>
  );
};

export default AddRaiseDeviationCapa;

